import React, { useEffect, useState } from "react";
import { DeliveryMan, CodSettlement, Store } from "@apis/index";
import Button from "@components/button";
import Row from "@components/row";
import Table from "@components/table";
import Title from "@components/title";
import { useModal } from "@context/ModalContext";
import styled from "@emotion/styled";
import { toast } from "react-toastify";
import { Formik, Form } from "formik";
import Fields from "@components/fields";
import Helper from "@utils/helper";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import {
  DollarSign,
  CheckCircle,
  Image as ImageIcon,
  Wallet,
  Banknote,
  CreditCard,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import Tabs from "@components/tabs";
import useConfig from "@hooks/useConfig";

const StatsCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatsLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const StatsValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
`;

const StatsTrend = styled.div<{ positive?: boolean }>`
  font-size: 12px;
  color: ${({ positive }) => (positive ? "#10b981" : "#6b7280")};
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const PageWrapper = styled.div`
  padding: 24px;
`;

export const HeaderArea = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

export const ModalOverlay = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

export const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
`;

export const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;

  label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  input,
  textarea,
  select {
    padding: 10px 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: all 0.2s;
    background: white;

    &:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
`;

export const ActionBtn = styled.button<{ variant?: "primary" | "success" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${({ variant }) =>
    variant === "success" ? "#ecfdf5" : "#eff6ff"};
  color: ${({ variant }) => (variant === "success" ? "#10b981" : "#3b82f6")};

  &:hover {
    background: ${({ variant }) =>
      variant === "success" ? "#d1fae5" : "#dbeafe"};
  }
`;

const CollectCashModal = ({
  user,
  payableToAdmin,
  onClose,
  onSuccess,
  settings,
}: {
  user: any;
  payableToAdmin: number;
  onClose: () => void;
  onSuccess: () => void;
  settings: any;
}) => {
  const [loading, setLoading] = useState(false);
  const owedAmount = payableToAdmin > 0 ? payableToAdmin : 0;

  const handleSubmit = async (values: any) => {
    if (values.amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      await CodSettlement.createAdminDirect({
        deliveryManId: user.id,
        amount: Number(values.amount),
        paymentMethod: values.paymentMethod,
        referenceId: values.referenceId || undefined,
        notes: values.notes || undefined,
      });
      toast.success("Cash collected successfully");
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to settle cash");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalTitle>Collect Cash from {user.name}</ModalTitle>
      <ModalSubtitle>
        Record physical cash handover or direct bank transfers verified by you.
      </ModalSubtitle>

      <div
        style={{
          marginBottom: "20px",
          background: "#f9fafb",
          padding: "16px",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span style={{ color: "#6b7280", fontSize: "14px" }}>
            Payable to Admin (COD)
          </span>
          <span
            style={{
              fontWeight: 600,
              color: owedAmount > 0 ? "#ef4444" : "#10b981",
            }}
          >
            {Helper.formatCurrency(owedAmount, settings)}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#6b7280", fontSize: "14px" }}>
            Wallet Balance
          </span>
          <span style={{ fontWeight: 500, color: "#111827" }}>
            {Helper.formatCurrency(user.walletBalance || 0, settings)}
          </span>
        </div>
      </div>

      <Formik
        initialValues={{
          amount: owedAmount > 0 ? owedAmount : "",
          paymentMethod: "Cash",
          referenceId: "",
          notes: "",
        }}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange }) => (
          <Form>
            <InputGroup>
              <label>Amount Collected *</label>
              <input
                name="amount"
                type="number"
                value={values.amount}
                onChange={handleChange}
                placeholder="0.00"
                min={0.01}
                step="0.01"
                required
              />
            </InputGroup>

            <InputGroup>
              <label>Payment Method *</label>
              <select
                name="paymentMethod"
                value={values.paymentMethod}
                onChange={handleChange}
                required
              >
                <option value="Cash">Physical Cash</option>
                <option value="Wallet">Wallet Balance (Auto-Approve)</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="eSewa">eSewa</option>
                <option value="Khalti">Khalti</option>
              </select>
            </InputGroup>

            <InputGroup>
              <label>Reference ID / Trx Code (Optional)</label>
              <input
                name="referenceId"
                type="text"
                value={values.referenceId}
                onChange={handleChange}
                placeholder="e.g. Trx-123456"
              />
            </InputGroup>

            <InputGroup>
              <label>Internal Notes (Optional)</label>
              <textarea
                name="notes"
                value={values.notes}
                onChange={handleChange}
                placeholder="Any comments about this handover..."
                rows={2}
              />
            </InputGroup>

            <Row styleRow="justify-content: flex-end; gap: 10px; margin-top: 24px;">
              <Button
                title="Cancel"
                onClick={onClose}
                bg="grey500"
                type="button"
              />
              <Button
                title={loading ? "Processing..." : "Confirm Collection"}
                type="submit"
                disabled={loading}
              />
            </Row>
          </Form>
        )}
      </Formik>
    </ModalOverlay>
  );
};


const ImagePreviewModal = ({
  url,
  onClose,
}: {
  url: string;
  onClose: () => void;
}) => (
  <ModalOverlay style={{ maxWidth: "600px" }}>
    <ModalTitle>Payment Proof</ModalTitle>
    <div
      style={{ marginTop: "16px", display: "flex", justifyContent: "center" }}
    >
      <img
        src={url}
        alt="Proof"
        style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: "8px" }}
      />
    </div>
    <Row styleRow="justify-content: flex-end; margin-top: 24px;">
      <Button title="Close" onClick={onClose} type="button" />
    </Row>
  </ModalOverlay>
);

const ApproveSettlementModal = ({
  settlement,
  onClose,
  onSuccess,
}: {
  settlement: any;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [loading, setLoading] = useState(false);

  const handleAction = async (status: "Approved" | "Rejected") => {
    setLoading(true);
    try {
      await CodSettlement.updateStatus(
        settlement.id,
        status,
        `Manually ${status.toLowerCase()} by admin`,
      );
      toast.success(`Settlement ${status} successfully`);
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalTitle>Verify Digital Payment</ModalTitle>
      <ModalSubtitle>
        Review this payment before clearing the driver's debt.
      </ModalSubtitle>

      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div>
          <strong>Method:</strong> {settlement.paymentMethod}
        </div>
        <div>
          <strong>Ref ID:</strong> {settlement.referenceId || "N/A"}
        </div>
        <div>
          <strong>Notes:</strong> {settlement.notes || "None"}
        </div>
      </div>

      <Row styleRow="justify-content: space-between; gap: 10px; margin-top: 24px;">
        <Button
          title={loading ? "Processing..." : "Reject"}
          onClick={() => handleAction("Rejected")}
          bg="red"
          type="button"
          disabled={loading}
        />
        <Button
          title={loading ? "Processing..." : "Approve & Clear Balance"}
          onClick={() => handleAction("Approved")}
          type="button"
          disabled={loading}
        />
      </Row>
    </ModalOverlay>
  );
};

// ----------------------------------------------------
// SUB-COMPONENTS FOR TABS
// ----------------------------------------------------
const CollectCashFormTab = () => {
  const [deliveryMen, setDeliveryMen] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [financials, setFinancials] = useState<{
    wallet: number;
    payable: number;
  } | null>(null);
  const [loadingFinancials, setLoadingFinancials] = useState(false);
  const { settings } = useGeneralSettings();

  const fetchUsers = async (type: string) => {
    if (!type) {
      setDeliveryMen([]);
      setStores([]);
      return;
    }
    setLoadingUsers(true);
    try {
      if (type === "deliveryman") {
        const res = await DeliveryMan.list({
          params: { limit: 1000, status: "Verified" },
        });
        if (res?.items) setDeliveryMen(res.items);
      } else if (type === "store") {
        const res = await Store.list({
          params: { limit: 1000, status: "Verified" },
        });
        if (res?.items) setStores(res.items);
      }
    } catch (e) {
      toast.error("Failed to fetch user list");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchFinancials = async (userId: string | number, type: string) => {
    if (!userId || !type) {
      setFinancials(null);
      return;
    }
    setLoadingFinancials(true);
    try {
      if (type === "store") {
        const storeObj = stores.find((s) => s.id === Number(userId));
        const res = await Store.getStoreAnalytics(Number(userId));
        setFinancials({
          wallet: storeObj?.walletBalance || 0,
          payable: res?.codFinancials?.payableToAdmin || 0,
        });
      } else {
        const res = await DeliveryMan.getAnalytics(Number(userId));
        setFinancials({
          wallet: res?.walletBalance || 0,
          payable: res?.codFinancials?.payableToAdmin || 0,
        });
      }
    } catch {
      setFinancials(null);
    } finally {
      setLoadingFinancials(false);
    }
  };

  const onSubmit = async (values: any, { resetForm }: any) => {
    if (
      !values.selectedType ||
      !values.selectedUser ||
      !values.amount ||
      !values.method ||
      Number(values.amount) <= 0
    ) {
      toast.error("Please fill in all details with a valid amount");
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        amount: Number(values.amount),
        paymentMethod:
          values.method.charAt(0).toUpperCase() + values.method.slice(1),
        notes: "Collected physically by Admin",
        ...(values.selectedType === "deliveryman" && {
          deliveryManId: Number(values.selectedUser),
        }),
        ...(values.selectedType === "store" && {
          storeId: Number(values.selectedUser),
        }),
      };
      await CodSettlement.createAdminDirect(data);
      toast.success("Cash collection recorded successfully");
      resetForm();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to settle cash");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        background: "white",
        padding: "24px",
        borderRadius: "12px",
        border: "1px solid #f3f4f6",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: 600,
          color: "#111827",
          marginBottom: "20px",
        }}
      >
        Record Physical Cash Collection
      </h3>

      <Formik
        initialValues={{
          selectedType: "",
          selectedUser: "",
          amount: "",
          method: "",
        }}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue }) => {
          const isStore = values.selectedType === "store";
          const dropdownOptions = isStore
            ? stores.map((s) => {
                const storeName = s.storeName || s.name || "Unnamed Store";
                const phone = s.storePhoneNo || s.phone;
                return {
                  id: s.id,
                  label: phone ? `${storeName} (${phone})` : storeName,
                };
              })
            : deliveryMen.map((d) => ({
                id: d.id,
                label: `${d.name} (${d.phoneNo})`,
              }));

          return (
            <Form
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Fields.DropDown
                    name="selectedType"
                    label="Select Origin"
                    placeholder="-- Choose User Type --"
                    options={[
                      { id: "deliveryman", title: "Delivery Man" },
                      { id: "store", title: "Store" },
                    ]}
                    valueAsKey="id"
                    titleAsKey="title"
                    onChange={(val) => {
                      setFieldValue("selectedType", val);
                      setFieldValue("selectedUser", "");
                      setFinancials(null);
                      fetchUsers(val);
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Fields.DropDown
                    name="selectedUser"
                    label={isStore ? "Select Store" : "Select User"}
                    placeholder={
                      loadingUsers ? "Loading..." : "-- Choose Entity --"
                    }
                    options={dropdownOptions}
                    valueAsKey="id"
                    titleAsKey="label"
                    hasSearch
                    onChange={(val) => {
                      setFieldValue("selectedUser", val);
                      fetchFinancials(val, values.selectedType);
                    }}
                    disabled={!values.selectedType || loadingUsers}
                    styleWrapper="width:96%"
                  />

                  {loadingFinancials ? (
                    <div
                      style={{
                        marginTop: "12px",
                        fontSize: "13px",
                        color: "#6b7280",
                      }}
                    >
                      Fetching financial details...
                    </div>
                  ) : financials ? (
                    <div
                      style={{
                        marginTop: "12px",
                        padding: "16px",
                        background: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        width: "96%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span style={{ color: "#475569", fontSize: "13px" }}>
                          Wallet Balance
                        </span>
                        <span style={{ fontWeight: 600, color: "#0f172a" }}>
                          {Helper.formatCurrency(financials.wallet, settings)}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ color: "#475569", fontSize: "13px" }}>
                          Payable to Admin
                        </span>
                        <span
                          style={{
                            fontWeight: 600,
                            color:
                              financials.payable > 0 ? "#ef4444" : "#10b981",
                          }}
                        >
                          {Helper.formatCurrency(financials.payable, settings)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                }}
              >
                <Fields.DropDown
                  name="method"
                  label="Payment Method"
                  options={[
                    { id: "cash", title: "Cash" },
                    { id: "wallet", title: "Wallet" },
                    { id: "bank", title: "Bank" },
                    { id: "esewa", title: "Esewa" },
                    { id: "khalti", title: "Khalti" },
                    { id: "fonepay", title: "Fonepay" },
                    { id: "imepay", title: "Imepay" },
                  ]}
                  valueAsKey="id"
                  titleAsKey="title"
                  placeholder="-- Choose Payment Method --"
                />
                <Fields.Input
                  name="amount"
                  label="Amount Collected"
                  type="number"
                  placeholder="0.00"
                />
              </div>

              <div>
                <Button
                  title={submitting ? "Processing..." : "Submit Collection"}
                  type="submit"
                  disabled={submitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const SettlementHistoryTab = () => {
  const { bucketUrl } = useConfig();
  const { settings } = useGeneralSettings();
  const { showModal, hideModal } = useModal();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>("");
  const [method, setMethod] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [data, setData] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await CodSettlement.list({
        params: {
          page,
          limit: 10,
          status: status || undefined,
          paymentMethod: method || undefined,
          search: search || undefined,
          type: type || undefined,
        },
      });
      if (res) {
        setData(res.data || []);
        if (res.meta) {
          setTotalItems(res.meta.total || 0);
          setTotalPages(res.meta.totalPages || 1);
        }
      }
    } catch {
      toast.error("Failed to fetch history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [page, status, method, search, type]);

  const columns = [
    {
      title: "Date",
      key: "date",
      render: (item: any) => (
        <div style={{ fontSize: "13px", color: "#4b5563" }}>
          {new Date(item.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      title: "Settlement For",
      key: "settlementFor",
      render: (item: any) => {
        if (item.deliveryMan && item.deliveryMan.name) {
          return (
            <div>
              <div
                style={{ fontWeight: 600, fontSize: "14px", color: "#111827" }}
              >
                {item.deliveryMan.name}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                Delivery Man
              </div>
            </div>
          );
        }
        if (item.store && item.store.name) {
          return (
            <div>
              <div
                style={{ fontWeight: 600, fontSize: "14px", color: "#111827" }}
              >
                {item.store.name}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>Store</div>
            </div>
          );
        }
        return <div style={{ color: "#9ca3af", fontSize: "13px" }}>N/A</div>;
      },
    },
    {
      title: "Type",
      key: "type",
      render: (item: any) => {
        const getIcon = () => {
          if (item.paymentMethod === "Wallet")
            return <Wallet size={16} color="#10b981" />;
          if (item.paymentMethod === "Cash")
            return <Banknote size={16} color="#3b82f6" />;
          return <CreditCard size={16} color="#8b5cf6" />;
        };
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {getIcon()}
            <div>
              <div
                style={{ fontWeight: 600, fontSize: "14px", color: "#111827" }}
              >
                {item.paymentMethod}
              </div>
              {item.referenceId && (
                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                  {item.referenceId}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Amount",
      key: "amount",
      style: { textAlign: "right" },
      render: (item: any) => (
        <div
          style={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "15px",
            textAlign: "right",
          }}
        >
          {Helper.formatCurrency(item.amount || 0, settings)}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      style: { textAlign: "center" },
      render: (item: any) => {
        const isWallet = item.paymentMethod === "Wallet";
        const statusVal =
          isWallet && item.status === "Approved"
            ? "Auto-Approved"
            : item.status;

        const c =
          statusVal === "Approved" || statusVal === "Auto-Approved"
            ? "#10b981"
            : statusVal === "Pending"
              ? "#f59e0b"
              : "#ef4444";
        const b =
          statusVal === "Approved" || statusVal === "Auto-Approved"
            ? "#ecfdf5"
            : statusVal === "Pending"
              ? "#fffbeb"
              : "#fef2f2";
        const Icon =
          statusVal === "Approved" || statusVal === "Auto-Approved"
            ? CheckCircle2
            : statusVal === "Pending"
              ? Clock
              : XCircle;

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <span
              style={{
                background: b,
                color: c,
                padding: "6px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                border: `1px solid ${c}20`,
              }}
            >
              <Icon size={14} />
              {statusVal}
            </span>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      style: { textAlign: "right" },
      render: (item: any) => (
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          {item.proofImage && (
            <ActionBtn
              onClick={() =>
                showModal(
                  <ImagePreviewModal
                    url={`${bucketUrl}/${item.proofImage}`}
                    onClose={hideModal}
                  />,
                )
              }
            >
              <ImageIcon size={14} /> View Proof
            </ActionBtn>
          )}
          {item.status === "Pending" && (
            <ActionBtn
              variant="success"
              onClick={() =>
                showModal(
                  <ApproveSettlementModal
                    settlement={item}
                    onClose={hideModal}
                    onSuccess={fetchList}
                  />,
                )
              }
            >
              <CheckCircle size={14} /> Verify
            </ActionBtn>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <Row styleRow="gap: 12px; margin-bottom: 20px; align-items: center; flex-wrap: wrap;">
        <div
          style={{
            display: "flex",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            overflow: "hidden",
            background: "white",
          }}
        >
          <input
            type="text"
            placeholder="Search Reference ID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{
              padding: "8px 12px",
              border: "none",
              outline: "none",
              fontSize: "14px",
              width: "200px",
            }}
          />
        </div>

        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          style={{
            padding: "8px 12px",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            fontSize: "14px",
            outline: "none",
            background: "white",
          }}
        >
          <option value="">All Types</option>
          <option value="store">Store</option>
          <option value="deliveryman">Delivery Man</option>
        </select>

        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          style={{
            padding: "8px 12px",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            fontSize: "14px",
            outline: "none",
            background: "white",
          }}
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>

        <select
          value={method}
          onChange={(e) => {
            setMethod(e.target.value);
            setPage(1);
          }}
          style={{
            padding: "8px 12px",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            fontSize: "14px",
            outline: "none",
            background: "white",
          }}
        >
          <option value="">All Methods</option>
          <option value="Wallet">Wallet</option>
          <option value="Cash">Cash</option>
          <option value="eSewa">eSewa</option>
          <option value="Bank Transfer">Bank Transfer</option>
        </select>
      </Row>

      <Table
        columns={columns}
        data={data}
        loading={loading}
        totalItems={totalItems}
        itemsPerPage={10}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={(newPage: number) => setPage(newPage)}
      />
    </div>
  );
};

// ----------------------------------------------------
// 3. MAIN PAGE COMPONENT
// ----------------------------------------------------
const CollectCashPage = () => {
  const { settings } = useGeneralSettings();
  const [stats, setStats] = useState({
    totalCod: 0,
    pendingCount: 0,
    autoApprovedCount: 0,
  });

  const fetchStats = async () => {
    try {
      const res = await CodSettlement.list({ params: { limit: 1000 } });
      if (res?.data) {
        const items = res.data;
        const total = items.reduce(
          (acc: number, curr: any) =>
            acc + (curr.status === "Approved" ? Number(curr.amount) : 0),
          0,
        );
        const pending = items.filter((i: any) => i.status === "Pending").length;
        const auto = items.filter(
          (i: any) => i.paymentMethod === "Wallet" && i.status === "Approved",
        ).length;
        setStats({
          totalCod: total,
          pendingCount: pending,
          autoApprovedCount: auto,
        });
      }
    } catch (e) {
      console.error("Failed to fetch stats", e);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const tabItems = [
    { label: "Collect Cash Form", component: CollectCashFormTab },
    { label: "Settlement History", component: SettlementHistoryTab },
  ];

  return (
    <PageWrapper>
      <HeaderArea>
        <Title textone="COD" texttwo="Settlements" />
      </HeaderArea>

      <Row styleRow="gap: 20px; margin-bottom: 32px; flex-wrap: wrap;">
        <StatsCard>
          <StatsLabel>Total COD Settled</StatsLabel>
          <StatsValue>
            {Helper.formatCurrency(stats.totalCod, settings)}
          </StatsValue>
          <StatsTrend positive>Complete Audit Trail</StatsTrend>
        </StatsCard>
        <StatsCard>
          <StatsLabel>Pending Verification</StatsLabel>
          <StatsValue>{stats.pendingCount}</StatsValue>
          <StatsTrend>Manual Review required</StatsTrend>
        </StatsCard>
        <StatsCard>
          <StatsLabel>Auto-Approved (Wallet)</StatsLabel>
          <StatsValue>{stats.autoApprovedCount}</StatsValue>
          <StatsTrend positive>Processed Instantly</StatsTrend>
        </StatsCard>
        <StatsCard>
          <StatsLabel>System Status</StatsLabel>
          <StatsValue>Online</StatsValue>
          <StatsTrend positive>Last updated: Just now</StatsTrend>
        </StatsCard>
      </Row>

      <div style={{ marginTop: "24px" }}>
        <Tabs items={tabItems} />
      </div>
    </PageWrapper>
  );
};

export default CollectCashPage;
